import { useRef } from 'react';
import type { Word } from '../../types/word.types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, Volume2, Quote } from 'lucide-react';
import { PartOfSpeechBadge } from './PartOfSpeechBadge';
import StarRating from './StarRating';
import { cn } from '@/lib/utils';

interface WordListItemProps {
  word: Word;
  onDelete: (wordId: string) => void;
  onRatingChange: (wordId: string, rating: number) => void;
  preview?: boolean;
}

function WordListItem({ word, onDelete, onRatingChange, preview = false }: WordListItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Get the phonetic from the word data (if available from the API)
  const phonetic = word.phonetic;

  // Get audio pronunciation (if available)
  const audioUrl = word.phonetics?.[0]?.audio;

  const handleDelete = () => {
    onDelete(word.id);
  };

  const handleRatingChange = (rating: number) => {
    onRatingChange(word.id, rating);
  };

  const playAudio = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(console.error);
    }
  };


  return (
    <Card className="mb-5">
      <CardContent className="p-4">
        {/* Header: Word + Part of Speech */}
        <div className="flex items-start justify-between gap-6 mb-2">
          <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {word.word}
                </h3>
                {phonetic && (
                  <span className="text-sm text-muted-foreground font-mono">
                    {phonetic}
                  </span>
                )}
                {audioUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={playAudio}
                    className="h-6 w-6 p-0"
                    aria-label="Play pronunciation"
                  >
                    <Volume2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            
            {/* Confidence Level - Hidden in preview mode */}
            {!preview && (
              <div className="flex items-center gap-1 mt-2 pb-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground font-medium">Confidence level:</span>
                <StarRating
                  rating={word.rating || 1}
                  onRatingChange={handleRatingChange}
                />
              </div>
            )}
          </div>

          {/* Action Buttons - Hidden in preview mode */}
          {!preview && (
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="text-muted-foreground hover:text-destructive hover:border-destructive"
                aria-label={`Delete ${word.word}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Definitions Section */}
        <div className="space-y-6 mt-3">
          <div
            ref={contentRef}
            className={cn(
              "transition-all duration-300 ease-in-out max-h-none"
            )}
          >
            {word.definitions.map((def, defIndex) => (
              <div key={defIndex} className="space-y-2 mb-3">
                {/* Part of Speech Header */}
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground text-sm font-bold rounded-full">
                    {defIndex + 1}
                  </span>
                  <PartOfSpeechBadge partOfSpeech={def.partOfSpeech} />
                </div>

                {/* Individual Definitions within this Part of Speech */}
                <div className="pl-11 space-y-3">
                  {def.definitions?.map((subDef, subIndex: number) => (
                    <div key={subIndex} className="space-y-2">
                      {/* Sub-definition */}
                      <div className="border-l-2 border-primary/20 pl-4">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs text-muted-foreground font-mono">
                            {defIndex + 1}.{subIndex + 1}
                          </span>
                          <p className="text-sm md:text-base text-foreground leading-relaxed font-medium flex-1">
                            {subDef.definition}
                          </p>
                        </div>
                      </div>

                      {/* Sub-definition Synonyms & Antonyms */}
                      {(subDef.synonyms && subDef.synonyms.length > 0) || (subDef.antonyms && subDef.antonyms.length > 0) ? (
                        <div className="pl-4 pt-2 space-y-2">
                          {subDef.synonyms && subDef.synonyms.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">≈</span>
                              {subDef.synonyms.slice(0, 4).map((synonym, synIndex) => (
                                <span
                                  key={synIndex}
                                  className="text-xs bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800"
                                >
                                  {synonym}
                                </span>
                              ))}
                              {subDef.synonyms.length > 4 && (
                                <span className="text-xs text-muted-foreground">+{subDef.synonyms.length - 4}</span>
                              )}
                            </div>
                          )}
                          {subDef.antonyms && subDef.antonyms.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-red-600 dark:text-red-400">≠</span>
                              {subDef.antonyms.slice(0, 4).map((antonym, antIndex) => (
                                <span
                                  key={antIndex}
                                  className="text-xs bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800"
                                >
                                  {antonym}
                                </span>
                              ))}
                              {subDef.antonyms.length > 4 && (
                                <span className="text-xs text-muted-foreground">+{subDef.antonyms.length - 4}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )) || (
                    /* Fallback for single definition (if definitions array doesn't exist) */
                    <div className="border-l-2 border-primary/20 pl-4">
                      <p className="text-sm md:text-base text-foreground leading-relaxed font-medium">
                        {def.meaning}
                      </p>
                    </div>
                  )}

                  {/* Overall Synonyms & Antonyms for this Part of Speech */}
                  {(def.synonyms && def.synonyms.length > 0) || (def.antonyms && def.antonyms.length > 0) ? (
                    <div className="border-t border-border/50 pt-2 space-y-1">
                      {def.synonyms && def.synonyms.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">≈</span>
                          {def.synonyms.slice(0, 6).map((synonym, synIndex) => (
                            <span
                              key={synIndex}
                              className="text-xs bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800"
                            >
                              {synonym}
                            </span>
                          ))}
                          {def.synonyms.length > 6 && (
                            <span className="text-xs text-muted-foreground">+{def.synonyms.length - 6}</span>
                          )}
                        </div>
                      )}
                      {def.antonyms && def.antonyms.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-red-600 dark:text-red-400">≠</span>
                          {def.antonyms.slice(0, 6).map((antonym, antIndex) => (
                            <span
                              key={antIndex}
                              className="text-xs bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800"
                            >
                              {antonym}
                            </span>
                          ))}
                          {def.antonyms.length > 6 && (
                            <span className="text-xs text-muted-foreground">+{def.antonyms.length - 6}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}

                  {/* Example (if it exists at the part of speech level) */}
                  {def.example && (
                    <div className="border-t border-border/30 pt-3 mt-3">
                      <div className="bg-blue-50/60 dark:bg-blue-950/20 rounded-lg p-3 border border-blue-200/40 dark:border-blue-800/40">
                        <div className="flex items-start gap-2">
                          <Quote className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground leading-relaxed pl-2 border-l border-blue-300/60 dark:border-blue-700/60">
                              {def.example.split(new RegExp(`\\b(${word.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\w*)\\b`, 'gi')).map((part, index) => {
                                const isMatch = new RegExp(`\\b${word.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\w*\\b`, 'gi').test(part);
                                return isMatch ? (
                                  <span key={index} className="underline decoration-2 decoration-blue-500 underline-offset-2 font-semibold">
                                    {part}
                                  </span>
                                ) : (
                                  part
                                );
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default WordListItem;
